'use client';
import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import type { EChartsOption } from 'echarts';
import type { ShortInterest } from '@benzinga/quotes-manager';
import { ChartPlotSkeleton } from './QuoteSkeletons';
const ReactECharts = React.lazy(() => import('echarts-for-react'));

interface ShortInterestTrendChartProps {
  shortInterest: ShortInterest[];
}

const ShortInterestTrendChart: React.FC<ShortInterestTrendChartProps> = ({ shortInterest }) => {
  const option = useMemo((): EChartsOption => {
    const sorted = [...shortInterest]
      .filter(record => record.recordDate && record.shortPercentOfFloat != null)
      .sort(
        (left, right) => new Date(left.recordDate).getTime() - new Date(right.recordDate).getTime(),
      );

    const labels = sorted.map(record => DateTime.fromISO(record.recordDate).toFormat('MMM yy'));
    const pctData = sorted.map(record => Number(record.shortPercentOfFloat));
    const sharesData = sorted.map(record => Number(record.totalShortInterest));

    return {
      grid: { left: 52, right: 16, top: 8, bottom: 56 },
      legend: { data: ['Short % of Float'], bottom: 0, textStyle: { fontSize: 10 } },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const first = rows[0] as { dataIndex?: number; axisValue?: string } | undefined;
          const dataIndex = first?.dataIndex ?? 0;
          const shares = sharesData[dataIndex];
          const pct = pctData[dataIndex];
          const sharesLabel =
            shares >= 1e6
              ? `${(shares / 1e6).toFixed(1)}M`
              : shares >= 1e3
                ? `${(shares / 1e3).toFixed(0)}K`
                : String(shares);
          return `<b>${first?.axisValue ?? ''}</b><br/>Short % of Float: <b>${pct?.toFixed(1)}%</b><br/>Shares Short: ${sharesLabel}`;
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (value: number) => `${value}%`, fontSize: 10 },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        {
          name: 'Short % of Float',
          type: 'line',
          data: pctData,
          lineStyle: { color: '#E05252', width: 2 },
          itemStyle: { color: '#E05252' },
          areaStyle: { color: 'rgba(224,82,82,0.10)' },
          smooth: true,
        },
      ],
    } as EChartsOption;
  }, [shortInterest]);

  if (!shortInterest.length) return null;

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Short Interest — % of Float
      </p>
      <React.Suspense fallback={<ChartPlotSkeleton label="Loading short interest chart" />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </React.Suspense>
    </div>
  );
};

export default ShortInterestTrendChart;
